/**
 * @cfleet/api-client - Auth Middleware
 *
 * OpenAPI-fetch middleware for automatic token attachment and refresh handling.
 *
 * Features:
 * - Attaches Bearer token to all authenticated requests
 * - Intercepts 401 responses and attempts token refresh
 * - Handles concurrent requests during refresh (request queuing)
 * - Multi-tenant support via X-Tenant-ID header
 * - Configurable callbacks for auth failure and token refresh
 *
 * @example
 * import createClient from 'openapi-fetch';
 * import { createAuthMiddleware, setTokens } from '@cfleet/api-client';
 *
 * const client = createClient<paths>({ baseUrl: '/api/v1' });
 * client.use(createAuthMiddleware({
 *   baseUrl: '/api/v1',
 *   onAuthFailure: () => navigate('/login'),
 * }));
 *
 * // After login
 * const { data } = await client.POST('/auth/login', { body: credentials });
 * setTokens(data.token, data.refresh_token, data.expires_in);
 */

import type { Middleware } from 'openapi-fetch';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  getTokenStorage,
} from './token-storage';
import { retryWithBackoff, type RetryOptions } from '../utils/retry-with-backoff';
import { enterRefresh, markReconnecting, exitRefresh, type RefreshState } from '../utils/refresh-state';

export type { RefreshState };

/**
 * Internal classification of a refresh failure so the retry policy and the
 * logout decision can be made without conflating "can't confirm" with "invalid".
 */
class RefreshFailure extends Error {
  constructor(
    readonly kind: 'unauthorized' | 'transient' | 'malformed',
    message: string
  ) {
    super(message);
    this.name = 'RefreshFailure';
  }
}

/**
 * Token response from the refresh endpoint
 * Supports both dispatcher/customer format (token) and driver format (access_token)
 */
export interface TokenResponse {
  /** JWT access token (dispatcher/customer format) */
  token?: string;
  /** JWT access token (driver format) */
  access_token?: string;
  /** Refresh token for obtaining new access tokens (may not be present if no rotation) */
  refresh_token?: string;
  /** Token expiry in seconds */
  expires_in: number;
  /** Token type (driver format) */
  token_type?: string;
}

/**
 * Configuration for the auth middleware
 */
export interface AuthMiddlewareConfig {
  /**
   * Base URL for the API.
   * Used to construct the refresh endpoint URL.
   */
  baseUrl: string;

  /**
   * Callback when authentication fails (refresh token expired/invalid).
   * Use this to redirect to login or show auth error UI.
   */
  onAuthFailure?: () => void;

  /**
   * Callback when tokens are successfully refreshed.
   * Use this to sync with external auth state (e.g., @cfleet/auth).
   */
  onTokenRefresh?: (tokens: TokenResponse) => void;

  /**
   * Path to the refresh endpoint relative to baseUrl.
   * @default '/auth/refresh'
   */
  refreshPath?: string;

  /**
   * Paths to exclude from 401 interception.
   * Auth endpoints are excluded by default.
   * @default ['/auth/login', '/auth/refresh', '/auth/password/reset']
   */
  excludedPaths?: string[];

  /**
   * Callback for refresh lifecycle transitions. Use this to drive a
   * non-destructive "reconnecting" UI while transient failures are retried.
   */
  onRefreshStateChange?: (state: RefreshState) => void;

  /**
   * Retry policy for transient refresh failures. Defaults to 3 attempts with
   * ~0.5/1s exponential backoff + jitter — worst-case well under the backend's
   * 10s refresh-token reuse window. Authoritative 401s are never retried.
   */
  refreshRetry?: RetryOptions;
}

/** Default refresh retry policy: 3 attempts, ~0.5s/1s backoff with jitter. */
const DEFAULT_REFRESH_RETRY: RetryOptions = {
  attempts: 3,
  initialDelayMs: 500,
  multiplier: 2,
  jitter: true,
};

/**
 * Default paths that should not trigger token refresh on 401
 */
const DEFAULT_EXCLUDED_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/password/reset',
  '/auth/password/reset/confirm',
  '/driver/auth/login',
  '/driver/auth/refresh',
  '/customer/auth/login',
  '/customer/auth/refresh',
];

/**
 * Creates an authentication middleware for openapi-fetch.
 *
 * The middleware:
 * 1. Attaches Bearer token to all requests (if token exists)
 * 2. Attaches X-Tenant-ID header for multi-tenant requests (if set)
 * 3. Intercepts 401 responses and attempts token refresh
 * 4. Retries the original request with new token after successful refresh
 * 5. Clears tokens and calls onAuthFailure if refresh fails
 *
 * @param config - Middleware configuration
 * @returns OpenAPI-fetch middleware
 *
 * @example
 * const client = createClient<paths>({ baseUrl: '/api/v1' });
 * client.use(createAuthMiddleware({
 *   baseUrl: '/api/v1',
 *   onAuthFailure: () => {
 *     window.location.href = '/login';
 *   },
 *   onTokenRefresh: (tokens) => {
 *     // Optionally sync with external state
 *     console.log('Tokens refreshed');
 *   },
 * }));
 */
export function createAuthMiddleware(config: AuthMiddlewareConfig): Middleware {
  const {
    baseUrl,
    onAuthFailure,
    onTokenRefresh,
    onRefreshStateChange,
    refreshPath = '/auth/refresh',
    excludedPaths = DEFAULT_EXCLUDED_PATHS,
    refreshRetry,
  } = config;

  const retryOptions: RetryOptions = { ...DEFAULT_REFRESH_RETRY, ...refreshRetry };

  // Cooldown after transient-refresh exhaustion. Suppress fresh refresh cycles
  // for one backoff interval so persistent-offline degrades to a single probe
  // per interval rather than every request re-starting the full retry ladder.
  const TRANSIENT_COOLDOWN_MS =
    (retryOptions.initialDelayMs ?? 500) *
    Math.pow(retryOptions.multiplier ?? 2, retryOptions.attempts ?? 3);
  let transientCooldownUntil: number | null = null;

  /** Route refresh lifecycle events to the counter-based shared channel. */
  function emitState(state: RefreshState): void {
    if (state === 'refreshing') enterRefresh();
    else if (state === 'reconnecting') markReconnecting();
    else if (state === 'idle') exitRefresh();
    onRefreshStateChange?.(state);
  }

  // State for handling concurrent refresh requests
  let isRefreshing = false;
  let refreshPromise: Promise<boolean> | null = null;

  /**
   * Perform a single refresh request, throwing a classified {@link RefreshFailure}
   * so the retry policy can distinguish authoritative failures (don't retry, log
   * out) from transient ones (retry with backoff) and malformed responses (give
   * up but keep the session — a server hiccup is not a logout).
   */
  async function refreshOnce(refreshToken: string): Promise<TokenResponse> {
    const refreshUrl = `${baseUrl}${refreshPath}`;
    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ refresh_token: refreshToken }),
      // 5 s hard cap — same reasoning as the adapter fetch: a stalled
      // connection must fail before the sidecar's 10 s replay window expires
      // so the retry can collect the cached response instead of getting 401.
      signal: AbortSignal.timeout(5000),
    });

    // 401 is the server authoritatively rejecting the session — don't retry.
    if (response.status === 401) {
      throw new RefreshFailure('unauthorized', 'Refresh token rejected (401)');
    }

    // Any other non-2xx (e.g. 503, 500, 502) is "can't confirm" — retryable.
    if (!response.ok) {
      throw new RefreshFailure('transient', `Refresh failed with status ${response.status}`);
    }

    let tokens: TokenResponse;
    try {
      tokens = (await response.json()) as TokenResponse;
    } catch {
      throw new RefreshFailure('malformed', 'Refresh response was not valid JSON');
    }

    // Handle both token field names (dispatcher/customer use 'token', driver uses 'access_token')
    const accessToken = tokens.token ?? tokens.access_token;
    if (!accessToken) {
      throw new RefreshFailure('malformed', 'Refresh response missing access token');
    }

    return tokens;
  }

  /**
   * Attempt to refresh the access token with retry-and-backoff.
   *
   * Uses a shared promise to prevent multiple concurrent refresh requests.
   * Logout (clearTokens + onAuthFailure) happens only on an authoritative
   * unauthenticated response. Transient failures are retried; if retries are
   * exhausted the session is left intact (the next request/tick can recover).
   */
  async function attemptRefresh(): Promise<boolean> {
    // If already refreshing, wait for the existing refresh to complete
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    // After transient exhaustion, suppress refresh attempts for one cooldown
    // interval so a screen full of queued requests doesn't each restart the
    // full retry ladder against a dependency that's already known to be down.
    if (transientCooldownUntil !== null && Date.now() < transientCooldownUntil) {
      return false;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    isRefreshing = true;
    emitState('refreshing');

    refreshPromise = (async () => {
      try {
        const tokens = await retryWithBackoff(() => refreshOnce(refreshToken), {
          ...retryOptions,
          // Authoritative + malformed failures short-circuit; only transient retries.
          isRetryable: (error) =>
            !(error instanceof RefreshFailure) || error.kind === 'transient',
          // First transient failure → signal a non-destructive "reconnecting" state.
          onRetry: () => emitState('reconnecting'),
        });

        // Get refresh token - may not be present if no rotation.
        // If not present, fall back to the original refresh token.
        const newRefreshToken = tokens.refresh_token ?? getRefreshToken();
        if (!newRefreshToken) {
          clearTokens();
          onAuthFailure?.();
          return false;
        }

        const accessToken = (tokens.token ?? tokens.access_token) as string;
        setTokens(accessToken, newRefreshToken, tokens.expires_in);
        onTokenRefresh?.(tokens);
        return true;
      } catch (error) {
        // Only an authoritative unauthenticated response logs the user out.
        // Transient (retries exhausted) or malformed failures preserve the
        // session so a later attempt can recover.
        if (error instanceof RefreshFailure && error.kind === 'unauthorized') {
          clearTokens();
          onAuthFailure?.();
        } else if (error instanceof RefreshFailure && error.kind === 'transient') {
          // All transient retries exhausted. Stamp a cooldown so subsequent
          // requests skip the retry ladder until the interval expires.
          transientCooldownUntil = Date.now() + TRANSIENT_COOLDOWN_MS;
        }
        return false;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
        emitState('idle');
      }
    })();

    return refreshPromise;
  }

  /**
   * Check if a path should be excluded from 401 interception
   */
  function isExcludedPath(url: string): boolean {
    return excludedPaths.some((path) => url.includes(path));
  }

  return {
    /**
     * Attach authorization headers to outgoing requests
     */
    async onRequest({ request }) {
      const token = getAccessToken();

      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      const tenantId = getTokenStorage().getTenantId();
      if (tenantId) {
        request.headers.set('X-Tenant-ID', tenantId);
      }

      return request;
    },

    /**
     * Intercept 401 responses and attempt token refresh
     */
    async onResponse({ request, response }) {
      // Only intercept 401 responses
      if (response.status !== 401) {
        return response;
      }

      // Don't intercept auth endpoints
      if (isExcludedPath(request.url)) {
        return response;
      }

      // Don't attempt refresh if no refresh token
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        onAuthFailure?.();
        return response;
      }

      // Attempt token refresh
      const refreshed = await attemptRefresh();

      if (!refreshed) {
        // Refresh failed - return original 401 response
        return response;
      }

      // Retry original request with new token
      const newToken = getAccessToken();
      if (!newToken) {
        return response;
      }

      // Clone the original request and update the authorization header
      // Note: Using new Request(request) preserves all properties including
      // method, headers, mode, credentials, etc. from the original request.
      // The headers from onRequest middleware are already applied.
      const retryRequest = new Request(request);
      retryRequest.headers.set('Authorization', `Bearer ${newToken}`);

      // Make the retry request directly using fetch
      // This bypasses openapi-fetch middleware chain but is the recommended
      // pattern per https://github.com/openapi-ts/openapi-typescript/discussions/2089
      return fetch(retryRequest);
    },
  };
}
